import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/Button';
import RarityBadge from '@/components/RarityBadge';
import { formatCurrency } from '@/utils/formatUtils';
import OfficialTag from '@/components/OfficialTag';
import { getProductThumbnailUrl } from '@/utils/imageUtils';

const ListingCard = ({ listing, onCancelListing }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const getGrade = () => {
    if (listing.psa) {
      return listing.psa.replace('PSA_', 'PSA ');
    }
    if (listing.bundledQuantity) {
      const unit = listing.product?.boxType === 'PACK' ? t('box.pack') : t('box.box_unit');
      return `${listing.bundledQuantity} ${unit}`;
    }
    if (listing.condition) {
      return t(`sell.product_info.conditions.${listing.condition.toLowerCase()}`);
    }
    return '-';
  };

  const handleEdit = () => {
    // Navigate to edit listing page
    navigate(`/listing/edit/${listing.id}`);
  };

  const handlePriceAdjust = () => {
    // Navigate to edit listing page with focus on price input
    navigate(`/listing/edit/${listing.id}?focus=price`);
  };

  const handleDetails = () => {
    // Save current page's scroll position before navigation
    // This ensures the scroll position is saved for back navigation
    // This is especially important when navigating via button click (not <a> tag)
    // because InfiniteScrollRestoration's click handler only processes <a> tags
    const currentPath = window.location.pathname + window.location.search;
    
    // Read scroll position synchronously to ensure we get the latest value
    // This should be done synchronously before any navigation to prevent race conditions
    const scrollY = window.scrollY || window.pageYOffset || 0;
    
    // Save immediately to prevent any race conditions with scroll listeners
    sessionStorage.setItem(`scroll_${currentPath}`, scrollY.toString());
    
    // Navigate after saving scroll position
    // If it's a non-PSA card, go to card listing page
    // If it's a PSA single card or box card, go to card detail page
    if (listing.condition && !listing.psa && listing.product.type === 'SINGLE_CARD') {
      // Non-PSA single card
      navigate(`/listing/${listing.id}`);
    } else {
      // PSA single card or box card
      navigate(`/card/${listing.product.id}`);
    }
  };

  const handleCancel = () => {
    if (onCancelListing) {
      onCancelListing(listing);
    }
  };

  return (
    <div className="grid grid-cols-[1fr,108px,108px,180px] gap-6 py-6">
      <div className="w-full flex justify-start items-center gap-4">
        <div className="w-[100px] h-[100px] relative bg-white rounded overflow-hidden">
          <img 
            src={getProductThumbnailUrl(listing.product.images?.[0]?.url)}
            alt={listing.product.localizedName}
            className="w-full h-full object-contain"
          />
        </div>
        <div className="flex-1 inline-flex flex-col justify-start items-start">
          <div>
            <div className="min-w-0">
              {listing.isOfficial && (
                <OfficialTag className="align-middle mr-1.5" />
              )}  
              <span className="inline align-middle text-[#111111] text-sm font-normal whitespace-normal break-words min-w-0">
                {listing.product.localizedRawName || listing.product.localizedName}
                {listing.product.rarityInfo?.sub?.name ? `(${listing.product.rarityInfo.sub.name})` : ''}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-1 mt-1">
              {listing.product.rarityInfo?.main && (
                <RarityBadge rarity={listing.product.rarityInfo.main} />
              )}
              {(listing.product?.set?.code || (listing.product?.rawCode && listing.product?.type !== 'CARD_BOX')) && (
                <div className="h-[18px] px-1 bg-[#f4f4f4] rounded-[3px] outline outline-[0.50px] outline-offset-[-0.50px] outline-[#dddddd] flex justify-start items-center gap-2.5">
                  <div className="justify-start text-[#111111] text-sm font-normal leading-[14px]">
                    {listing.product?.type === 'CARD_BOX' ? listing.product?.set?.code : (listing.product?.set?.code && listing.product?.rawCode ? `${listing.product.set.code} ${listing.product.rawCode}` : listing.product?.set?.code || listing.product?.rawCode)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center text-[#666666] text-sm font-normal">
        {getGrade()}
      </div>
      <div className="flex justify-center items-center text-center text-[#666666] text-sm font-normal">
        {formatCurrency(listing.price)}
      </div>
      <div className="inline-flex flex-col justify-center items-center gap-1.5">
        <Button
          variant="default"
          size="s"
          className="w-20 mb-1 px-0"
          onClick={handleEdit}
        >
          {t('common.edit')}
        </Button>
        <Button
          variant="default"
          size="s"
          className="w-20 mb-1 px-0"
          onClick={handleDetails}
        >
          {t('common.details')}
        </Button>
      </div>
    </div>
  );
};

export default ListingCard; 
